import { useRef } from "react";
import { Control, FormProvider, Path, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { showErrorToast } from "#app/notifications";
import { useCompany } from "#modules/company/view/useCompany";
import { UpdateProcessStepResponseDto } from "#modules/processSteps/infra/updateProcessStep.api";
import { useUpdateProcessStep } from "#modules/processSteps/view/useUpdateProcessStep";
import { useClickOutside } from "#shared/helpers/useClickOutside";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { getProcessStepTitle } from "#shared/view/components/Form/getProcessStepTitle";
import { Stack } from "#shared/view/components/Stack";
import { Title } from "#shared/view/components/Title";
import { Button } from "#shared/view/designSystem/Button";
import { NumberInputController } from "#shared/view/designSystem/inputs/NumberInputController";
import { RecommendedPriceInputController } from "#shared/view/designSystem/inputs/RecommendedPriceInputController";
import { TextInputController } from "#shared/view/designSystem/inputs/TextInputController";
import { TimeInputController } from "#shared/view/designSystem/inputs/TimeInputController";
import {
  assertUnreachable,
  MaterialGradeType,
  ProcessStepModeSchema,
  TimeDisplayFormatSchema,
  TimeDisplayFormatType,
} from "shared";
import { IndustrialProcessSchema } from "shared";

import { PartialProcessStep } from "./ProcessStep";

type Props = {
  processStep: PartialProcessStep;
  onClose: () => void;
  onProcessStepUpdated?: (updatedProcessStep: UpdateProcessStepResponseDto) => void;
  materialGrade: MaterialGradeType | null;
  quoteRequestLineId?: string;
};

export const TimeProcessStepForm = ({
  processStep,
  materialGrade,
  onProcessStepUpdated,
  onClose,
  quoteRequestLineId,
}: Props) => {
  const { t } = useTranslation();
  const { company } = useCompany();
  const analytics = useAnalytics();

  const formMethods = useForm<PartialProcessStep>({
    defaultValues: processStep,
  });

  const { handleSubmit, control } = formMethods;

  const { updateProcessStep } = useUpdateProcessStep({
    onSuccess: onProcessStepUpdated,
  });

  const onSubmit: SubmitHandler<PartialProcessStep> = (payload) => {
    if (!processStep.id || !payload.id) {
      showErrorToast({ title: "Une erreur est survenue" });

      return;
    }

    const {
      machineHourlyPrice,
      machinePreparationTime,
      otherMachineTime,
      fixedCost,
      fixedCostPerSeries,
      description,
    } = payload;

    analytics.capture(AnalyticsEvent.DetailsPanelProcessStepEdited, {
      quote_request_line_id: quoteRequestLineId,
      process_step_id: processStep.id,
      industrial_process: processStep.industrialProcess,
      field: "form",
    });

    updateProcessStep({
      id: processStep.id,
      payload: {
        machineHourlyPrice,
        machinePreparationTime,
        otherMachineTime,
        fixedCost,
        fixedCostPerSeries,
        description,
      },
    });
    onClose();
  };

  const title = getProcessStepTitle({ processStep, companyMachine: undefined, materialGrade });

  const machinePreparationTimeRightSectionRef = useRef<HTMLDivElement>(null);
  const otherMachineTimeInputRightSectionRef = useRef<HTMLDivElement>(null);

  const formRef = useClickOutside<HTMLDivElement>(onClose, null, [
    machinePreparationTimeRightSectionRef,
    otherMachineTimeInputRightSectionRef,
  ]);

  if (
    (processStep.industrialProcess === IndustrialProcessSchema.enum.CONTROL &&
      processStep.fixedCostPerSeries) ||
    (processStep.industrialProcess === IndustrialProcessSchema.enum.SURFACE_TREATMENT &&
      processStep.fixedCost !== null &&
      processStep.fixedCost !== undefined) ||
    processStep.industrialProcess === IndustrialProcessSchema.enum.OUTSOURCING ||
    (processStep.industrialProcess === IndustrialProcessSchema.enum.TRANSPORT &&
      processStep.fixedCostPerSeries)
  ) {
    return (
      <Stack gap="xs" ref={formRef}>
        <Title order={6}>{title}</Title>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap="xxs">
              <RecommendedPriceInputController
                name="fixedCostPerSeries"
                label={t("processStep.fixedCostPerSeries")}
                unit="€"
                control={control}
                computedValue={{ value: processStep.fixedCostPerSeries }}
              />
              <RecommendedPriceInputController
                name="fixedCost"
                label={t("processStep.fixedCost")}
                unit="€"
                control={control}
                computedValue={{ value: processStep.fixedCost }}
              />
              <Button type="submit">{t("common.validate")}</Button>
            </Stack>
          </form>
        </FormProvider>
      </Stack>
    );
  }

  if (
    processStep.mode !== ProcessStepModeSchema.enum.TIME ||
    (processStep.fixedCostPerSeries || 0) > 0
  ) {
    return null;
  }

  const timeDisplayFormat = company?.companySettings.timeDisplayFormat;

  return (
    <Stack gap="xs" ref={formRef}>
      <Title order={6}>{title}</Title>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap="xxs">
            {processStep.industrialProcess === IndustrialProcessSchema.Enum.OTHER && (
              <TextInputController
                name="description"
                label={t("processStep.description")}
                control={control}
              />
            )}
            <ProcessStepTimeInputController
              name="machinePreparationTime"
              label={t("processStep.machinePreparationTime")}
              control={control}
              timeDisplayFormat={timeDisplayFormat}
            />
            <ProcessStepTimeInputController
              name="otherMachineTime"
              label={t("processStep.otherMachineTime")}
              control={control}
              timeDisplayFormat={timeDisplayFormat}
            />
            <RecommendedPriceInputController
              name="machineHourlyPrice"
              label={t("processStep.machineHourlyPrice")}
              control={control}
              computedValue={{ value: processStep.machineHourlyPrice }}
            />
            <Button type="submit">{t("common.validate")}</Button>
          </Stack>
        </form>
      </FormProvider>
    </Stack>
  );
};

const ProcessStepTimeInputController = (props: {
  timeDisplayFormat: TimeDisplayFormatType | undefined;
  name: Path<PartialProcessStep>;
  label: string;
  control: Control<PartialProcessStep>;
}) => {
  const { timeDisplayFormat, ...rest } = props;
  switch (timeDisplayFormat) {
    case TimeDisplayFormatSchema.enum.HH_MM_SS:
    case undefined:
      return <TimeInputController {...rest} />;
    case TimeDisplayFormatSchema.enum.DECIMAL_MINUTES:
      return <NumberInputController {...rest} />;
    case TimeDisplayFormatSchema.enum.DECIMAL_HOURS:
      return <NumberInputController {...rest} defaultUnitFactor={{ value: 1 / 60, unit: null }} />;
    default: {
      assertUnreachable(timeDisplayFormat);
    }
  }
};
